// PostSubscribeAndUnlockButton.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import Loading from '../component/Loading.jsx';
import Trans from '../component/Trans.jsx';
import { ButtonId } from '../resource/trackingConstants.js';
import { DrmFeatureName } from '../resource/drmConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import ButtonShimmerStyle from '../style/ButtonShimmerStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { color, textColor } from '../style/variables.js';

import DiamondIconSource from '../../img/ic-diamond-xs.svg';

import { QUICKPAY_BUNDLE_SECTION } from '../resource/shopConstants.js';

export class PostSubscribeAndUnlockButton extends React.PureComponent {
  getIsSubscribed = () => {
    const { senderId, meId, timestampOffsetSeconds, meSubscription } =
      this.props;
    const { nbf, exp } = meSubscription;
    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    return meId && (meId === senderId || (now >= nbf && now < exp));
  };
  render() {
    const {
      meId,
      postId,
      unlockPrice,
      canViewMedia: propsCanViewMedia,
      unlockButtonStatus,
      t,
      sendUnlockMessage,
      hasPurchased,
      isBalanceInsufficient,
      openQuickpayModal,
    } = this.props;
    const isSubscribed = this.getIsSubscribed();
    const isAuthed = !!meId;
    const canViewMedia = isSubscribed || propsCanViewMedia;
    const shouldShowShimmer = !hasPurchased && isBalanceInsufficient;
    if (unlockPrice === 0 || (isAuthed && canViewMedia)) {
      return null;
    }
    const handleClickUnlock = () => {
      if (!isAuthed || (!hasPurchased && isBalanceInsufficient)) {
        openQuickpayModal({
          sectionId: QUICKPAY_BUNDLE_SECTION.POST,
        });
        return;
      }
      return sendUnlockMessage({
        messageId: postId,
        shouldCheckDrm: true,
        drmFeatureName: DrmFeatureName.POST,
      });
    };
    return (
      <StyledPostSubscribeAndUnlockButton>
        <UnlockButton
          data-element_id={
            !isAuthed
              ? canViewMedia
                ? ButtonId.Detail.ButtonFreeDetailSignup
                : ButtonId.Detail.ButtonDetailSignup
              : ButtonId.All.ButtonUnlock
          }
          hasSubscriptionPlan={false}
          shouldShowShimmer={shouldShowShimmer}
          onClick={handleClickUnlock}
        >
          {!isAuthed || (!hasPurchased && isBalanceInsufficient) ? (
            t('cta_unlock_post_zero_purchase', {
              ns: TranslationNamespace.LIVESTREAM,
            })
          ) : 'loading' === unlockButtonStatus ? (
            <Loading />
          ) : (
            <Trans
              defaults={t('unlock_post', {
                diamonds: unlockPrice,
                ns: TranslationNamespace.POST,
              }).replace(unlockPrice, '<0>{unlockPrice}</0>')}
              values={{ unlockPrice }}
              components={[
                <UnlockPrice key="unlockPrice">
                  <DiamondIcon src={DiamondIconSource} alt="diamond icon" />
                  <span>{unlockPrice}</span>
                </UnlockPrice>,
              ]}
            />
          )}
        </UnlockButton>
      </StyledPostSubscribeAndUnlockButton>
    );
  }
}

PostSubscribeAndUnlockButton.propTypes = {
  meId: PropTypes.string,
  postId: PropTypes.string,
  senderId: PropTypes.string,
  unlockButtonStatus: PropTypes.string,
  unlockPrice: PropTypes.number,
  timestampOffsetSeconds: PropTypes.number,
  canViewMedia: PropTypes.bool,
  hasPurchased: PropTypes.bool,
  isBalanceInsufficient: PropTypes.bool,
  meSubscription: PropTypes.object,
  t: PropTypes.func.isRequired,
  sendUnlockMessage: PropTypes.func,
  openQuickpayModal: PropTypes.func,
};

PostSubscribeAndUnlockButton.defaultProps = {
  meId: null,
  postId: null,
  senderId: null,
  unlockPrice: null,
  timestampOffsetSeconds: 0,
  unlockButtonStatus: null,
  canViewMedia: false,
  hasPurchased: false,
  isBalanceInsufficient: false,
  meSubscription: {},
  sendUnlockMessage: () => null,
  openQuickpayModal: () => null,
};

const StyledPostSubscribeAndUnlockButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UnlockButton = styled(
  ({ hasSubscriptionPlan, isFilled, shouldShowShimmer, ...restProps }) => (
    <button {...restProps} />
  )
).attrs(({ hasSubscriptionPlan, isFilled }) => ({
  style: {
    border: hasSubscriptionPlan ? 'unset' : `1px solid ${color.tealBlue}`,
    borderRadius: hasSubscriptionPlan ? 'unset' : '4px',
    width: hasSubscriptionPlan ? 'unset' : '100%',
    height: hasSubscriptionPlan ? 'unset' : '40px',
    color: isFilled
      ? color.neutral[0]
      : hasSubscriptionPlan
        ? 'rgba(255, 255, 255, 0.5)'
        : textColor.primary,
    backgroundColor: isFilled ? color.tealBlue : 'transparent',
  },
}))`
  ${ResetButtonStyle};
  ${({ shouldShowShimmer }) => shouldShowShimmer && ButtonShimmerStyle};
  --button-shine-color: ${color.tealBlue_alpha_10};
  margin-bottom: 16px;
  max-width: 600px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
`;

const UnlockPrice = styled.span`
  ${TextEllipsis};
  margin-left: 8px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  color: ${textColor.primary};
  font-size: 16px;
  font-weight: 600;
`;

const DiamondIcon = styled.img`
  margin-right: 4px;
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

export default withTranslation()(PostSubscribeAndUnlockButton);
